import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import { useSelector } from "react-redux";
import CustomButton from "../../component/ui/FormFields/Button";
import { useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  BOOKING_PASSENGER_DETAILS,
  BOOKINGS,
  DEV_PAYMENT_PROCESSING,
  EDIT_BOOKING_SUCCESS_PAGE,
} from "../../component/common/Routes/AppRoutes";
import {
  VENDOR_BOOKING_PASSENGER_DETAILS,
  VENDOR_BOOKINGS,
} from "../../component/common/Routes/VendorRoutes";
import {
  CUSTOMER_BOOKING_PASSENGER_DETAILS,
  CUSTOMER_BOOKINGS,
} from "../../component/common/Routes/CustomerRoutes";
import {
  editBookingRequest,
  fetchBookingRequest,
  resetBookingDetails,
} from "../../redux/Bookings/BookingAction";
import "../../styles/UserStyles.css";
import Loading from "../../component/ui/Loading";
import {
  convertDateFormat,
  convertDateFormatBooking,
  convertStaffDateFormat,
  formatDateWithAmPm,
} from "../../Style";
import dayjs from "dayjs";

const EditBooking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tab = location.state?.tabEdit ?? null;
  const page = location.state?.pageEdit ?? null;
  const loading = useSelector((state) => state.booking?.loading);
  const bookingData = location.state?.bookingData || {};
  const { bookingId } = useParams();
  const [applyPenalty, setApplyPenalty] = useState(false);
  const [penaltyAmount, setPenaltyAmount] = useState("");

  useEffect(() => {
    dispatch(fetchBookingRequest(bookingId));
    dispatch(resetBookingDetails());
  }, [dispatch, bookingId]);

  const bookingDetails = useSelector(
    (state) => state.booking?.booking?.data[0]
  );
  const user = useSelector((state) => state.auth?.user);

  // Changed: Make isInstantBooking dynamic based on booking type
  const isInstantBooking = bookingDetails?.booked_type === 3; // 3 is instant booking type

  const editBookingDetail = useSelector((state) => state.booking?.editBooking);
  const paymentUrl = editBookingDetail?.data?.paymentUrl;
  const editBookingLoading = editBookingDetail?.loading;
  const success = editBookingDetail?.success;
  const error = editBookingDetail?.error;

  const {
    formState: { errors },
    control,
    handleSubmit,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      departure_date:
        convertDateFormat(bookingDetails?.BookingDetail?.date_of_departure) ||
        null,
      departure_time: bookingDetails?.BookingDetail?.time_of_departure || "",
      arrival_date:
        convertDateFormat(bookingDetails?.BookingDetail?.date_of_arrival) ||
        null,
      arrival_time: bookingDetails?.BookingDetail?.time_of_arrival || "",
      arrival_flight_number:
        bookingDetails?.BookingDetail?.arrival_flight_number || "",
      departure_flight_number:
        bookingDetails?.BookingDetail?.departure_flight_number || "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (bookingDetails?.BookingDetail) {
      const details = bookingDetails.BookingDetail;
      setValue(
        "departure_date",
        convertDateFormatBooking(details.date_of_departure)
      );
      setValue("departure_time", details.time_of_departure);
      setValue(
        "arrival_date",
        convertDateFormatBooking(details.date_of_arrival)
      );
      setValue("arrival_time", details.time_of_arrival);
      setValue("arrival_flight_number", details.arrival_flight_number);
      setValue("departure_flight_number", details.departure_flight_number);
    }
  }, [bookingDetails, setValue]);

  const formValues = watch();

  const serviceType = bookingDetails?.Service?.type_of_name || "";

  const arrivalFields = [
    {
      name: "arrival_flight_number",
      label: "Arrival Flight Number *",
      placeholder: "",
      rules: { required: "Flight Number is required" },
      type: "text",
      sm: 12,
    },
    {
      name: "arrival_date",
      label: "Arrival Date *",
      placeholder: "",
      type: "datebooking",
      sm: 6,
      rules: {
        required: "Date of Arrival is required",
        validate: (value) => {
          if (!value) return "Date is required";

          // Parse the DD-MM-YYYY formatted string
          const selectedDate = dayjs(value, "DD-MM-YYYY");

          // Check if the date is valid
          if (!selectedDate.isValid()) {
            return "Invalid date format";
          }

          const today = dayjs().startOf("day");

          // Always check for past dates
          if (selectedDate.isBefore(today)) {
            return "Date cannot be in the past";
          }

          // For non-instant booking, enforce 48-hour rule
          // if (!isInstantBooking) {
          //   const minAllowedDate = dayjs().add(48, "hours").startOf("day");
          //   if (selectedDate.isBefore(minAllowedDate)) {
          //     return "Arrival date must be at least 48 hours from now";
          //   }
          // }

          return true;
        },
      },
    },
    {
      name: "arrival_time",
      label: "Arrival Time *",
      placeholder: "",
      type: "time",
      sm: 6,
      rules: {
        required: "Time of Arrival is required",
        validate: (_, formValues) => {
          const { arrival_date, arrival_time } = formValues;

          if (!arrival_date || !arrival_time)
            return "Date and Time are required";

          // Parse DD-MM-YYYY format properly
          const [day, month, year] = arrival_date.split("-");
          // Create a date string in YYYY-MM-DD format that JS can parse
          const dateFormatted = `${year}-${month}-${day}`;

          const selectedDateTime = new Date(`${dateFormatted}T${arrival_time}`);
          const now = new Date();
          return selectedDateTime >= now || "Time cannot be in the past";
          // For instant booking, allow any future time (not past)
          // if (isInstantBooking) {
          //   return selectedDateTime >= now || "Time cannot be in the past";
          // } else {
          //   // For non-instant booking, enforce 48-hour rule
          //   const minAllowedDateTime = new Date();
          //   minAllowedDateTime.setHours(minAllowedDateTime.getHours() + 48);
          //   return selectedDateTime >= minAllowedDateTime ||
          //     "Arrival must be at least 48 hours from now";
          // }
        },
      },
    },
    bookingDetails?.booked_type !== 2 && {
      name: "penalty_charge",
      label: "Express charges *",
      placeholder: "Enter express charges",
      type: "Number",
      sm: 12,
      rules: {
        required: "Express charges is required",
        validate: (value) => {
          if (value <= 0) return "Amount must be greater than zero";
          return true;
        },
      },
    },
  ];

  const departureFields = [
    {
      name: "departure_flight_number",
      label: "Departure Flight Number *",
      placeholder: "",
      rules: { required: "Flight Number is required" },
      type: "text",
      sm: 12,
    },
    {
      name: "departure_date",
      label: "Departure Date *",
      placeholder: "",
      type: "datebooking",
      sm: 6,
      rules: {
        required: "Date of Departure is required",
        validate: (value) => {
          if (!value) return "Date is required";

          // Parse the DD-MM-YYYY formatted string
          const selectedDate = dayjs(value, "DD-MM-YYYY");

          // Check if the date is valid
          if (!selectedDate.isValid()) {
            return "Invalid date format";
          }

          const today = dayjs().startOf("day");

          // Always check for past dates
          if (selectedDate.isBefore(today)) {
            return "Date cannot be in the past";
          }

          // // For non-instant booking, enforce 48-hour rule
          // if (!isInstantBooking) {
          //   const minAllowedDate = dayjs().add(48, "hours").startOf("day");
          //   if (selectedDate.isBefore(minAllowedDate)) {
          //     return "Departure date must be at least 48 hours from now";
          //   }
          // }

          return true;
        },
      },
    },
    {
      name: "departure_time",
      label: "Departure Time *",
      placeholder: "",
      rules: {
        required: "Time of Departure is required",
        validate: (_, formValues) => {
          const { departure_date, departure_time } = formValues;

          if (!departure_date || !departure_time)
            return "Date and Time are required";

          // Parse DD-MM-YYYY format properly
          const [day, month, year] = departure_date.split("-");
          // Create a date string in YYYY-MM-DD format that JS can parse
          const dateFormatted = `${year}-${month}-${day}`;

          const selectedDateTime = new Date(
            `${dateFormatted}T${departure_time}`
          );
          const now = new Date();
          return selectedDateTime >= now || "Time cannot be in the past";
          // Changed: For instant booking, allow any future time (not past)
          // if (isInstantBooking) {
          //   return selectedDateTime >= now || "Time cannot be in the past";
          // } else {
          //   // For non-instant booking, enforce 48-hour rule
          //   const minAllowedDateTime = new Date();
          //   minAllowedDateTime.setHours(minAllowedDateTime.getHours() + 48);
          //   return selectedDateTime >= minAllowedDateTime ||
          //     "Departure must be at least 48 hours from now";
          // }
        },
      },
      type: "time",
      sm: 6,
    },
    bookingDetails?.booked_type !== 2 && {
      name: "penalty_charge",
      label: "Express charges *",
      placeholder: "Enter express charges",
      type: "Number",
      sm: 12,
      rules: {
        required: "Express charges is required",
        validate: (value) => {
          if (value <= 0) return "Amount must be greater than zero";
          return true;
        },
      },
    },
  ];

  const transitFields = [
    {
      name: "arrival_flight_number",
      label: "Arrival Flight Number *",
      placeholder: "",
      rules: { required: "Flight Number is required" },
      type: "text",
      sm: 6,
    },
    {
      name: "departure_flight_number",
      label: "Departure Flight Number *",
      placeholder: "",
      rules: { required: "Flight Number is required" },
      type: "text",
      sm: 6,
    },
    {
      name: "arrival_date",
      label: "Arrival Date *",
      placeholder: "",
      rules: {
        required: "Date of Arrival is required",
        validate: (value) => {
          if (!value) return "Date is required";

          // Parse the DD-MM-YYYY formatted string
          const selectedDate = dayjs(value, "DD-MM-YYYY");

          // Check if the date is valid
          if (!selectedDate.isValid()) {
            return "Invalid date format";
          }

          const today = dayjs().startOf("day");

          // Changed: For instant booking, only check for past dates
          if (selectedDate.isBefore(today)) {
            return "Date cannot be in the past";
          }

          // Changed: For non-instant booking, enforce 48-hour rule
          // if (!isInstantBooking) {
          //   const minAllowedDate = dayjs().add(48, "hours").startOf("day");
          //   if (selectedDate.isBefore(minAllowedDate)) {
          //     return "Arrival date must be at least 48 hours from now";
          //   }
          // }

          return true;
        },
      },
      type: "datebooking",
      sm: 6,
    },
    {
      name: "departure_date",
      label: "Departure Date *",
      placeholder: "",
      rules: {
        required: "Date of Departure is required",
        validate: (value, formValues) => {
          if (!value) return "Date is required";

          // Parse the DD-MM-YYYY formatted string
          const selectedDayjsDate = dayjs(value, "DD-MM-YYYY");
          if (!selectedDayjsDate.isValid()) {
            return "Invalid date format";
          }

          // Convert to JS Date for existing logic
          const selectedDate = selectedDayjsDate.toDate().setHours(0, 0, 0, 0);
          const today = new Date().setHours(0, 0, 0, 0);

          // Changed: Always check for past dates
          if (selectedDate < today) {
            return "Date cannot be in the past";
          }

          // For Transit bookings, check relation to arrival date
          if (
            formValues.arrival_departure_type?.value === "Transit" &&
            formValues.arrival_date
          ) {
            const departureDate = selectedDayjsDate
              .toDate()
              .setHours(0, 0, 0, 0);
            const arrivalDate = dayjs(formValues.arrival_date, "DD-MM-YYYY")
              .toDate()
              .setHours(0, 0, 0, 0);

            if (departureDate < arrivalDate) {
              return "Departure date must be on or after arrival date";
            }
          }

          return true;
        },
      },
      type: "datebooking",
      sm: 6,
    },
    {
      name: "arrival_time",
      label: "Arrival Time *",
      placeholder: "",
      type: "time",
      sm: 6,
      rules: {
        required: "Time of Arrival is required",
        validate: (_, formValues) => {
          const { arrival_date, arrival_time } = formValues;

          if (!arrival_date || !arrival_time)
            return "Date and Time are required";

          // Parse DD-MM-YYYY format properly
          const [day, month, year] = arrival_date.split("-");
          // Create a date string in YYYY-MM-DD format that JS can parse
          const dateFormatted = `${year}-${month}-${day}`;

          const selectedDateTime = new Date(`${dateFormatted}T${arrival_time}`);
          const now = new Date();
          return selectedDateTime >= now || "Time cannot be in the past";
          // Changed: For instant booking, allow any future time (not past)
          // if (isInstantBooking) {
          //   return selectedDateTime >= now || "Time cannot be in the past";
          // } else {
          //   // For non-instant booking, enforce 48-hour rule
          //   const minAllowedDateTime = new Date();
          //   minAllowedDateTime.setHours(minAllowedDateTime.getHours() + 48);
          //   return selectedDateTime >= minAllowedDateTime ||
          //     "Arrival must be at least 48 hours from now";
          // }
        },
      },
    },
    {
      name: "departure_time",
      label: "Departure Time *",
      placeholder: "",
      rules: {
        required: "Time of Departure is required",
        validate: (value, formValues) => {
          const { departure_date, departure_time } = formValues;

          if (!departure_date || !departure_time) {
            return "Date and Time are required";
          }

          // Parse using dayjs
          const departureDateTime = dayjs(
            `${departure_date} ${departure_time}`,
            "DD-MM-YYYY HH:mm"
          );

          if (!departureDateTime.isValid()) {
            return "Invalid departure date or time format";
          }

          const now = dayjs();
          if (!departureDateTime.isAfter(now)) {
           
            return "Time cannot be in the past";
          }
          console.log(formValues);
                if (
                     
                      formValues.arrival_date &&
                      formValues.arrival_time
                    ) {
                      const arrivalDateTime = dayjs(
                        `${formValues.arrival_date} ${formValues.arrival_time}`,
                        "DD-MM-YYYY HH:mm"
                      );
          
                      if (!arrivalDateTime.isValid()) {
                        return "Invalid arrival date or time format";
                      }
          
                      const diffHours = departureDateTime.diff(
                        arrivalDateTime,
                        "hour",
                        true
                      );
                      console.log(diffHours,"DIFF")
                      if (diffHours < 3) {
                        return "Transit time must be at least 3 hours";
                      }
          
                      if (diffHours > 6) {
                        return "Transit time cannot exceed 6 hours";
                      }
                    }
          
                    return true;
          // Changed: For instant booking, allow any future time (not past)
          // if (isInstantBooking) {
          //   if (!departureDateTime.isAfter(now)) {
          //     return "Time cannot be in the past";
          //   }
          // } else {
          //   // For non-instant booking, enforce 48-hour rule
          //   const minAllowedDateTime = now.add(48, "hour");
          //   if (!departureDateTime.isAfter(minAllowedDateTime)) {
          //     return "Departure must be at least 48 hours from now";
          //   }
          // }

          // Transit time constraint
          if (
            formValues.arrival_departure_type?.value === "Transit" &&
            formValues.arrival_date &&
            formValues.arrival_time
          ) {
            const arrivalDateTime = dayjs(
              `${formValues.arrival_date} ${formValues.arrival_time}`,
              "DD-MM-YYYY HH:mm"
            );

            if (!arrivalDateTime.isValid()) {
              return "Invalid arrival date or time format";
            }

            const diffHours = departureDateTime.diff(
              arrivalDateTime,
              "hour",
              true
            );

            if (diffHours < 3) {
              return "Transit time must be at least 3 hours";
            }

            if (diffHours > 6) {
              return "Transit time cannot exceed 6 hours";
            }
          }

          return true;
        },
      },
      type: "time",
      sm: 6,
    },
    bookingDetails?.booked_type !== 2 && {
      name: "penalty_charge",
      label: "Express charges *",
      placeholder: "Enter Express charges",
      type: "Number",
      sm: 12,
      rules: {
        required: "Express charges is required",
        validate: (value) => {
          if (value <= 0) return "Amount must be greater than zero";
          return true;
        },
      },
    },
  ];

  const getFields = () => {
    if (serviceType === "Arrival" || serviceType === "arrival") {
      return arrivalFields;
    } else if (serviceType === "Departure" || serviceType === "departure") {
      return departureFields;
    } else if (serviceType === "Transit" || serviceType === "transit") {
      return transitFields;
    }
  };

  const getRoute = () => {
    if (user?.role === "Admin" || user?.role === "Staff") {
      dispatch(resetBookingDetails());
      return BOOKINGS;
    } else if (user?.role === "Vendor") {
      dispatch(resetBookingDetails());
      return VENDOR_BOOKINGS;
    } else if (user?.role === "Customer") {
      dispatch(resetBookingDetails());
      return CUSTOMER_BOOKINGS;
    }
    return BOOKINGS;
  };

  const onSubmit = async (data) => {
    const updatedBooking = {
      date_of_departure: convertDateFormatBooking(data.departure_date) || null,
      time_of_departure: data.departure_time || "1",
      date_of_arrival: convertDateFormatBooking(data.arrival_date) || null,
      time_of_arrival: data.arrival_time || "1",
      departure_flight_number: data.departure_flight_number || null,
      arrival_flight_number: data.arrival_flight_number || null,
      penalty_charge: data?.penalty_charge || "0",
      date_of_transit: null,
      time_of_transit: null,
      success_url: DEV_PAYMENT_PROCESSING,
      failure_url: DEV_PAYMENT_PROCESSING,
    };

    dispatch(editBookingRequest({ bookingId, updatedData: updatedBooking }));
  };

  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-center",
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: false,
      });
    }
  }, [error, success, navigate]);

  useEffect(() => {
    if (success) {
      toast.success("Booking date and time updated successfully", {
        position: "top-center",
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: false,
        onClose: () => {
          dispatch(resetBookingDetails());
          navigate(BOOKINGS);
        },
      });
    }
    if (success && paymentUrl && bookingDetails?.booked_type === 2) {
      dispatch(resetBookingDetails());
      navigate(EDIT_BOOKING_SUCCESS_PAGE);
    } else if (
      success &&
      paymentUrl &&
      (user?.role === "Admin" || user?.role === "Staff")
    ) {
      dispatch(resetBookingDetails());
      navigate(EDIT_BOOKING_SUCCESS_PAGE, {
        state: { BookingType: bookingDetails?.booked_type },
      });
    } else if (success && paymentUrl) {
      dispatch(resetBookingDetails());
      window.location.href = paymentUrl;
    }
  }, [paymentUrl]);

  if (loading) {
    return <Loading text="Loading Booking details..." />;
  }
  const canApplyPenalty = user?.role === "Admin" || user?.role === "Staff";

  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Box component={Paper} className="add_airport_details_heading">
          <Typography variant="h5" className="form-heading">
            Edit Booking
          </Typography>
          <Box className="form-margin">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={4}>
                {getFields()?.map((field, index) => (
                  <Grid item xs={12} sm={field.sm} key={index}>
                    <ControlledTextInputField
                      name={field.name}
                      control={control}
                      rules={field.rules}
                      type={field.type}
                      instantBooking={true}
                      label={field.label}
                      placeholder={field.placeholder}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]?.message}
                      setValue={setValue}
                    />
                  </Grid>
                ))}
                <Grid item xs={12} className="button-container">
                  <CustomButton
                    variant="contained"
                    className="grey-button"
                    text="Cancel"
                    onClick={() =>
                      tab !== null
                        ? navigate(getRoute(), {
                            state: { tab: tab, page: page },
                          })
                        : navigate(-1)
                    }
                  />
                  <CustomButton
                    variant="contained"
                    className="orange-button"
                    text="Save Changes"
                    type="submit"
                    loading={editBookingLoading}
                  />
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default EditBooking;
